/**
 * タスクの手順書を読むためのポップアップ。
 *
 * 手順書は月次経理の型（FMT）側で書き、タスクからは読むだけにする。毎月同じ作業を
 * するときに、別のシートを開かずその場で手順を確かめられるようにするためのもの。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクの手順書を、画面中央のポップアップで読めるようにする。
 * @graph-connects none
 */
import { useEffect } from 'react';
import { ManualView } from './ManualView';

/**
 * TaskManual の props。
 */
export interface TaskManualProps {
  /** 見出しに出すタスク名 */
  title: string;
  /** 手順書の中身（HTML） */
  manual: string;
  /** 閉じるときに呼ばれる */
  onClose: () => void;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクの手順書をポップアップで読めるようにする
 * @graph-connects ManualView [calls] 手順書の HTML を描く
 */
export function TaskManual({ title, manual, onClose }: TaskManualProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="マニュアルを閉じる"
        onClick={onClose}
        className="fixed inset-0 z-30 cursor-default bg-[var(--color-text-primary)]/20"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="マニュアル"
        className="fixed top-1/2 left-1/2 z-40 flex max-h-[85vh] w-[min(44rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] shadow-[0_8px_32px_var(--color-shadow)]"
      >
        <header className="flex items-start justify-between gap-3 border-b border-[var(--color-border-subtle)] px-6 py-4">
          <div className="min-w-0">
            <p className="m-0 text-xs text-[var(--color-text-muted)]">マニュアル</p>
            <h2 className="m-0 truncate text-base font-semibold text-[var(--color-text-primary)]">
              {title}
            </h2>
          </div>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="-mt-1 shrink-0 px-1 text-lg leading-none text-[var(--color-text-muted)]"
          >
            ×
          </button>
        </header>

        <ManualView manual={manual} className="overflow-y-auto px-6 py-4" />

        {/* 閉じるのは見出しの × ・Esc ・背景のどれでも。ほかのポップアップに合わせる */}
        <footer className="border-t border-[var(--color-border-subtle)] px-6 py-3">
          <p className="m-0 text-xs text-[var(--color-text-muted)]">
            直すときは「月次経理」の一覧から、この項目を開いてください
          </p>
        </footer>
      </aside>
    </>
  );
}
